import Layout from "@/components/Layout";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";

const reviews = [
  {
    author: "Sophie-May Ward-Marchbank",
    context: "Strand Magazine · Chapter 1, The Crypt Gallery, London",
    publication: "Strand Magazine",
    href: "https://www.strandmagazine.co.uk/single-post/nick-fraser-at-the-crypt-gallery-resurrecting-east-london-s-van-gogh",
    headline: "Nick Fraser at The Crypt Gallery: Resurrecting East London's Van Gogh",
    paragraphs: [
      "I booked to see Nick Fraser, Chapter 1 at The Crypt Gallery a few weeks ago after seeing a plastered poster advertising its presence on Fleet Street. I walk that street nearly every day on my way to university and hence have become somewhat blind to its sights and bulletins. This one however, drew my eye; most likely it was the combination of the quiet Catholic overtones in the painting used where the man in flame-like colours raises his eyes skywards, palms up as if in prayer, that I always seem to catch on and the epithet: \"the East End's Van Gogh\". Religion, art, and East London, in some ways my own holy trinity, I had to make a point of attending.",
      "I won't say I had no expectations for the exhibition; as with anything I wanted to enjoy my experience, but I knew relatively little before going in on Fraser, his work, or its later reception and resurgence, and I certainly did not expect to be moved in the way that I was. The Crypt Gallery is located underneath the St Pancras Parish Church, which in its Greek Revival style is a treat to see before even getting into the heart of the gallery itself. Although small, the gallery manages to house a decent number of works and uses its size to its advantage, with the pieces perfectly at eye level and every alcove taken advantage of to be used as framing for each work.",
      "The art itself was unforgettable, first shown again here after 30 years it was clearly inspired by sacrality yet embellished with a deeply personal, invigorated spirit, expressive and fluid swirls and dots contained in thick black outlines rendering the pieces at once maverick and articulate, clearly made by a man with multitudes in his soul and a wise mind to translate them into visions. The pieces depict various beings, all outlined in black and coloured with deep browns, oranges, and yellows with flecks of jewel red and blue. Most paintings seem to have taken inspiration from biblical scenes like the Last Supper or the gates of heaven.",
      "From afar the paintings can look eerie and piercing, with jewel-tone eyes and wide mouths frozen in what could be screams, but on looking close up, there's an unrestrained joy to the loops and curls, in colours that seem to laugh in just the same way that the figures' open mouths also look to be doing. There is a consciousness of a wider universal experience fringing the paintings, a handshake with the metaphysical, a song of otherworldly oneness. Yet still underneath the spiritual splendour was a man who came from the humble East End, working long hours on an assembly line, finding time and money wherever he could to make art.",
      "If I had left the gallery having seen only the paintings I would have been more than content, having been moved by Fraser's skill and poignancy, however in the furthest room from the entrance, a video was playing on a loop. Once I had waited for the previous showing to end the projector hit some technical difficulties so I decided to seek out somebody in the gallery who could fix it so that I could watch. I ended up finding Marcus O'Shaughnessy, the curator of the exhibition, who was walking around the corridors of the little gallery exuding pride and a glowing fondness for the works that accompanied his steps.",
      "Nick Fraser Chapter 1 at The Crypt Gallery is truly one of the most full-bodied, tender, and devoted installations I have attended in the past year; triumphant in both the art and careful curation, an artist to remember, and a curator to watch.",
    ],
  },
  {
    author: "Artemis Van Dorssen",
    context: "Raw Vision · Issue 121",
    publication: "Raw Vision",
    href: "https://rawvision.com",
    headline: "Nick Fraser in Raw Vision, Issue 121",
    paragraphs: [
      "Featured in the international journal of intuitive, outsider and visionary art — a written review of Nick Fraser's life and work by Artemis Van Dorssen, placing Fraser among the contemporary outsider artists shaping the field.",
    ],
  },
  {
    author: "Andrianne",
    context: "Visitor · Chapter 1, The Crypt Gallery, London",
    paragraphs: [
      "I'm still contemplating the mesmerising artworks of Nick Fraser, whose posthumous exhibition at the Crypt Gallery in London this week left me stunned. Spiritual, tribal, ethereal, a faintly ominous other-worldly experience. Perhaps the atmospheric live music and the Crypt setting itself enhanced the sense of vacuous blackness in the empty eyes and gaping mouths — the dramatic legacy of an unrecognised yet extraordinary talent who died in 2023, unknown. His story is now beautifully and lovingly revealed by lifelong friend and muse, Mark O'Shaughnessy and his son, Marcus, who curated this first public exhibition of Nick Fraser artist, Chapter 1, in the artist's home city, London. World class. Congratulations.",
    ],
  },
];

export default function Reviews() {
  return (
    <Layout>
      <main data-testid="reviews-page" className="bg-[color:var(--bone)]">
        <PageHeader
          eyebrow="Reviews"
          title="What people have said."
          lede="Selected writing and visitor impressions on Nick Fraser's life and work — reproduced from the published reviews and testimonials gathered by the estate."
          image="/release/p101-ii.jpg"
        />

        <section className="bone-section py-24 md:py-40">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 space-y-32 md:space-y-48">
            {reviews.map((r, i) => (
              <Reveal key={i} slow>
                <article data-testid={`review-${i}`}>
                  <p className="hairline opacity-60 mb-3">{r.context}</p>
                  {r.headline && (
                    <h2 className="font-serif italic text-3xl md:text-4xl leading-[1.1] mt-2 mb-8 max-w-[24ch]">
                      {r.headline}
                    </h2>
                  )}
                  <div className="space-y-6 md:space-y-7 max-w-[64ch]">
                    {r.paragraphs.map((p, j) => (
                      <p
                        key={j}
                        className={`font-serif ${
                          r.paragraphs.length === 1
                            ? "italic text-2xl md:text-4xl leading-[1.25]"
                            : "text-lg md:text-xl leading-[1.6] opacity-90"
                        }`}
                        data-testid={`review-para-${i}-${j}`}
                      >
                        {r.paragraphs.length === 1 ? `"${p}"` : p}
                      </p>
                    ))}
                  </div>
                  <div className="rule mt-12 mb-6 opacity-30 max-w-xs" />
                  <p className="font-serif italic text-xl">{r.author}</p>
                  {r.publication && (
                    <p className="hairline opacity-50 mt-2">{r.publication}</p>
                  )}
                  {r.href && (
                    <a
                      href={r.href}
                      target="_blank"
                      rel="noreferrer"
                      className="link-quiet font-serif italic text-base md:text-lg mt-6 inline-block"
                      data-testid={`review-link-${i}`}
                    >
                      Read the full review →
                    </a>
                  )}
                </article>
              </Reveal>
            ))}
          </div>
        </section>
      </main>
    </Layout>
  );
}
